import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Loader2, CheckCircle2, XCircle } from "lucide-react";
import { api } from "../lib/api";
import { useCart } from "../context/CartContext";

const Success = () => {
  const [params] = useSearchParams();
  const [status, setStatus] = useState("checking"); // checking, paid, failed
  const [details, setDetails] = useState(null);
  const [attempts, setAttempts] = useState(0);
  const { clear } = useCart();

  const sessionId = params.get("session_id");

  useEffect(() => {
    if (!sessionId) {
      setStatus("failed");
      return;
    }
    const poll = async () => {
      if (attempts >= 15) {
        // Stripe only redirects here AFTER payment, so don't scare the customer:
        // the webhook will finalize it. Show a reassuring pending state instead.
        setStatus("pending");
        clear();
        return;
      }
      try {
        const { data } = await api.get(`/checkout/status/${sessionId}`);
        setDetails(data);
        if (data.payment_status === "paid") {
          setStatus("paid");
          clear();
          return;
        }
        if (data.status === "expired") {
          setStatus("failed");
          return;
        }
        setTimeout(() => setAttempts(attempts + 1), 2000);
      } catch {
        setTimeout(() => setAttempts(attempts + 1), 2000);
      }
    };
    poll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, attempts]);

  return (
    <main data-testid="success-page" className="max-w-2xl mx-auto px-5 py-20 text-center">
      {status === "checking" && (
        <>
          <Loader2 size={48} className="animate-spin text-[#EC008C] mx-auto mb-4" />
          <h1 className="font-display font-black text-3xl text-[#000000]">Confirming payment...</h1>
          <p className="text-[#333333] mt-2">Hang tight, we're checking with Stripe.</p>
        </>
      )}
      {status === "paid" && (
        <>
          <CheckCircle2 size={64} className="text-[#CCDE00] mx-auto mb-4" />
          <h1 className="font-display font-black text-4xl md:text-5xl text-[#000000]">Thank you! 🎉</h1>
          <p className="text-[#333333] mt-3 text-lg">
            Your order is confirmed. We'll email you tracking as soon as it ships.
          </p>
          {details && (
            <div className="brutal-static bg-[#F3E30A] rounded-3xl p-6 mt-8 text-left">
              <div className="text-xs font-bold uppercase tracking-widest text-[#000000]/70">Order Summary</div>
              <div className="mt-2 font-display font-black text-2xl text-[#000000]">
                ${(details.amount_total / 100).toFixed(2)} {details.currency?.toUpperCase()}
              </div>
            </div>
          )}
          <div className="mt-8 flex gap-3 justify-center">
            <Link to="/shop" data-testid="success-shop-more" className="bg-[#EC008C] text-[#FFFFFF] font-bold px-6 py-3 rounded-full brutal">
              Keep shopping
            </Link>
            <Link to="/" data-testid="success-home" className="bg-white text-[#000000] font-bold px-6 py-3 rounded-full brutal">
              Home
            </Link>
          </div>
        </>
      )}
      {status === "pending" && (
        <>
          <CheckCircle2 size={64} className="text-[#CCDE00] mx-auto mb-4" />
          <h1 className="font-display font-black text-3xl md:text-4xl text-[#000000]">Payment received</h1>
          <p className="text-[#333333] mt-3 text-lg">
            We're finalizing your order now. A confirmation email is on its way — this can take a moment.
          </p>
          <div className="mt-8 flex gap-3 justify-center">
            <button
              onClick={() => { setAttempts(0); setStatus("checking"); }}
              data-testid="success-refresh"
              className="bg-[#000000] text-white font-bold px-6 py-3 rounded-full brutal"
            >
              Refresh status
            </button>
            <Link to="/account" data-testid="success-account" className="bg-[#EC008C] text-white font-bold px-6 py-3 rounded-full brutal">
              View my orders
            </Link>
          </div>
        </>
      )}
      {status === "failed" && (
        <>
          <XCircle size={64} className="text-[#EC008C] mx-auto mb-4" />
          <h1 className="font-display font-black text-3xl text-[#000000]">Payment not confirmed</h1>
          <p className="text-[#333333] mt-2">Something went wrong. Try again or contact us.</p>
          <Link to="/cart" data-testid="failed-cart" className="mt-6 inline-block bg-[#EC008C] text-[#FFFFFF] font-bold px-6 py-3 rounded-full brutal">
            Back to cart
          </Link>
        </>
      )}
    </main>
  );
};

export default Success;
